import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useAtom } from 'jotai'
import { currentOrganizationAtom } from '../store/organizationAtoms'
import { userService } from '../services/firestoreService'
import { StorageService } from '../services/storageService'
import Sidebar from '../components/layout/Sidebar'
import { auth } from '../config/firebase'
import { updateProfile } from 'firebase/auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import SlidingToggle from '@/components/ui/SlidingToggle'
import { 
  User, 
  Settings, 
  Save,
  Camera,
  Shield,
  Sun,
  Moon,
  Monitor,
  BarChart3,
  Palette
} from 'lucide-react'
import type { User as UserType, UpdateUser } from '@shared/schemas/user'
import ChartPreferencesSettings from '../components/settings/ChartPreferencesSettings'
import ColorPreferencesSettings from '../components/settings/ColorPreferencesSettings'
import { defaultChartPreferences } from '@shared/schemas/chartPreferences'
import type { ChartPreferences } from '@shared/schemas/chartPreferences'
import type { ColorPalette } from '../lib/colorPalettes'
import { authService } from '../services/authService'
import { claimsRefreshService } from '../services/claimsRefreshService'
import { debugService } from '../services/debugService'
import { PaletteManagementUI } from '@/components/settings/PaletteManagementUI'
import { simpleTestServiceCall } from '@/services/paletteService'
import { useToast } from '@/hooks/use-toast'
import { PaletteSeedingComponent } from '@/components/dev/PaletteSeedingComponent'

// Timezone options
const TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
  { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (London)' },
  { value: 'Europe/Paris', label: 'Central European Time (Paris)' },
  { value: 'Europe/Berlin', label: 'Central European Time (Berlin)' },
  { value: 'Europe/Rome', label: 'Central European Time (Rome)' },
  { value: 'Europe/Madrid', label: 'Central European Time (Madrid)' },
  { value: 'Europe/Amsterdam', label: 'Central European Time (Amsterdam)' },
  { value: 'Europe/Stockholm', label: 'Central European Time (Stockholm)' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time (Helsinki)' },
  { value: 'Europe/Moscow', label: 'Moscow Standard Time' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time' },
  { value: 'Asia/Singapore', label: 'Singapore Standard Time' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time (Seoul)' },
  { value: 'Asia/Mumbai', label: 'India Standard Time (Mumbai)' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)' },
  { value: 'Australia/Perth', label: 'Australian Western Time (Perth)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (Auckland)' },
  { value: 'America/Sao_Paulo', label: 'Brasília Time (São Paulo)' },
  { value: 'America/Mexico_City', label: 'Central Standard Time (Mexico City)' },
  { value: 'Africa/Cairo', label: 'Eastern European Time (Cairo)' },
  { value: 'Africa/Johannesburg', label: 'South Africa Standard Time' }
]

// Language options
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español (Spanish)' },
  { value: 'fr', label: 'Français (French)' },
  { value: 'de', label: 'Deutsch (German)' },
  { value: 'it', label: 'Italiano (Italian)' },
  { value: 'pt', label: 'Português (Portuguese)' },
  { value: 'ru', label: 'Русский (Russian)' },
  { value: 'ja', label: '日本語 (Japanese)' },
  { value: 'ko', label: '한국어 (Korean)' },
  { value: 'zh', label: '中文 (Chinese)' },
  { value: 'ar', label: 'العربية (Arabic)' },
  { value: 'hi', label: 'हिन्दी (Hindi)' },
  { value: 'nl', label: 'Nederlands (Dutch)' },
  { value: 'sv', label: 'Svenska (Swedish)' },
  { value: 'no', label: 'Norsk (Norwegian)' },
  { value: 'da', label: 'Dansk (Danish)' },
  { value: 'fi', label: 'Suomi (Finnish)' },
  { value: 'pl', label: 'Polski (Polish)' },
  { value: 'tr', label: 'Türkçe (Turkish)' },
  { value: 'th', label: 'ไทย (Thai)' },
  { value: 'vi', label: 'Tiếng Việt (Vietnamese)' },
  { value: 'id', label: 'Bahasa Indonesia (Indonesian)' },
  { value: 'ms', label: 'Bahasa Melayu (Malay)' },
  { value: 'he', label: 'עברית (Hebrew)' },
  { value: 'cs', label: 'Čeština (Czech)' },
  { value: 'hu', label: 'Magyar (Hungarian)' },
  { value: 'ro', label: 'Română (Romanian)' },
  { value: 'bg', label: 'Български (Bulgarian)' },
  { value: 'hr', label: 'Hrvatski (Croatian)' },
  { value: 'sk', label: 'Slovenčina (Slovak)' },
  { value: 'sl', label: 'Slovenščina (Slovenian)' },
  { value: 'et', label: 'Eesti (Estonian)' },
  { value: 'lv', label: 'Latviešu (Latvian)' },
  { value: 'lt', label: 'Lietuvių (Lithuanian)' },
  { value: 'uk', label: 'Українська (Ukrainian)' },
  { value: 'el', label: 'Ελληνικά (Greek)' }
]

const Profile: React.FC = () => {
  const { currentUser } = useAuth()
  const { theme, setTheme } = useTheme()
  const [currentOrganization] = useAtom(currentOrganizationAtom)
  const [userProfile, setUserProfile] = useState<UserType | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')
  const [formData, setFormData] = useState<UpdateUser>({})
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const { toast } = useToast()

  const resolvedChartPreferences = useMemo((): ChartPreferences => {
    const userChartPreferences = userProfile?.chartPreferences;

    // Ensure we have a base that matches ChartPreferences structure, even if user prefs are absent or partial
    const resolved: ChartPreferences = {
      tooltipFormatting: {
        ...defaultChartPreferences.tooltipFormatting,
        ...(userChartPreferences?.tooltipFormatting || {}),
      },
      displayPreferences: {
        ...defaultChartPreferences.displayPreferences,
        ...(userChartPreferences?.displayPreferences || {}),
      },
    };
    return resolved;
  }, [userProfile?.chartPreferences]);

  // Development helper functions will remain globally available on window object during development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).authService = authService;
      (window as any).claimsRefreshService = claimsRefreshService;
      (window as any).callTestFunction = async () => {
        console.log('🚀 Calling simpleTestCallable with payload "Test Me from Profile Colors Tab"...');
        try {
          const result = await simpleTestServiceCall('Test Me from Profile Colors Tab');
          console.log('✅ simpleTestCallable Response:', result);
        } catch (error) {
          console.error('❌ simpleTestCallable Error:', error);
        }
      };
      (window as any).refreshClaims = async () => {
        if (!currentUser?.uid) {
          console.error('❌ No authenticated user found');
          return;
        }
        try {
          await claimsRefreshService.refreshUserClaims(currentUser.uid);
          console.log('🎉 Claims refresh completed! Try your palette creation again.');
        } catch (error) {
          console.error('❌ Claims refresh failed:', error);
        }
      };
      (window as any).checkClaims = async () => {
        try {
          const claims = await claimsRefreshService.checkCurrentUserClaims();
          console.log('📋 Your current claims:', claims);
        } catch (error) {
          console.error('❌ Failed to check claims:', error);
        }
      };
      (window as any).debugMemberships = async () => {
        if (!currentUser?.uid) {
          console.error('❌ No authenticated user found');
          return;
        }
        try {
          // From previous debugging, we know these values for your user
          const knownOrganizationId = 'k8w9QrmKO3K9VEeSehyA'; 
          const knownRole = 'owner';

          console.log(`🚀 Attempting to set claims directly for org: ${knownOrganizationId}, role: ${knownRole}`);
          await claimsRefreshService.refreshUserClaims(currentUser.uid, knownOrganizationId, knownRole);
          
          console.log('✅ Claims refresh attempted. Now check your claims with checkClaims()');
          
          // Optionally, re-run the membership check to see if Firestore data is still consistent
          // await debugService.checkUserMemberships(currentUser.uid);
          // await debugService.checkOrganizations();
        } catch (error) {
          console.error('❌ Debug & direct claims set failed:', error);
        }
      };
      console.log('🛠️ Development helpers available on window object (e.g., window.callTestFunction()).');
    }
  }, [currentUser]);

  // Load user profile and custom palettes on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!currentUser?.uid) return
      
      try {
        setLoading(true)
        const profile = await userService.getById(currentUser.uid)
        
        if (profile) {
          setUserProfile(profile)
          setFormData({
            displayName: profile.displayName || '',
            phoneNumber: profile.phoneNumber || '',
            bio: profile.bio || '',
            title: profile.title || '',
            location: profile.location || '',
            website: profile.website || '',
            timezone: profile.timezone || '',
            language: profile.language || 'en',
            theme: theme || profile.theme || 'system'
          })
        } else {
          // Create user profile if it doesn't exist
          const newProfile = await userService.createOrUpdate(currentUser.uid, {
            email: currentUser.email || '',
            displayName: currentUser.displayName || '',
            photoURL: currentUser.photoURL || undefined,
            language: 'en',
            theme: theme || 'system'
          })
          setUserProfile(newProfile)
          setFormData({
            displayName: newProfile.displayName || '',
            phoneNumber: newProfile.phoneNumber || '',
            bio: newProfile.bio || '',
            title: newProfile.title || '',
            location: newProfile.location || '',
            website: newProfile.website || '',
            timezone: newProfile.timezone || '',
            language: newProfile.language || 'en',
            theme: theme || newProfile.theme || 'system'
          })
        }
      } catch (err) {
        console.error('Error loading user profile:', err)
        setError('Failed to load user profile')
      } finally {
        setLoading(false)
      }
    }

    loadUserProfile()
  }, [currentUser])

  const handleInputChange = (field: keyof UpdateUser, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSaveProfile = async () => {
    if (!currentUser?.uid) return
    
    try {
      setSaving(true)
      setError('')
      setSuccess('')
      
      await userService.update(currentUser.uid, formData)
      
      // Reload the profile to get updated data
      const updatedProfile = await userService.getById(currentUser.uid)
      if (updatedProfile) {
        setUserProfile(updatedProfile)
        // Update form data with the latest saved data
        setFormData({
          displayName: updatedProfile.displayName || '',
          phoneNumber: updatedProfile.phoneNumber || '',
          bio: updatedProfile.bio || '',
          title: updatedProfile.title || '',
          location: updatedProfile.location || '',
          website: updatedProfile.website || '',
          timezone: updatedProfile.timezone || '',
          language: updatedProfile.language || 'en',
          theme: theme || updatedProfile.theme || 'system'
        })
      }
      
      setSuccess('Profile updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('❌ Error updating profile:', err)
      setError('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handlePreferenceChange = async (preference: keyof NonNullable<UserType['preferences']>, value: boolean) => {
    if (!currentUser?.uid) return
    
    try {
      await userService.updatePreferences(currentUser.uid, {
        [preference]: value
      })
      
      // Update local state
      if (userProfile) {
        const currentPreferences = userProfile.preferences || {
          emailNotifications: true,
          pushNotifications: true,
          marketingEmails: false,
          weeklyDigest: true
        };
        setUserProfile({
          ...userProfile,
          preferences: {
            ...currentPreferences,
            [preference]: value
          }
        })
      }
    } catch (err) {
      console.error('Error updating preferences:', err)
      setError('Failed to update preferences')
    }
  }

  const handleThemeChange = async (newTheme: string) => {
    const themeValue = newTheme as 'light' | 'dark' | 'system'
    
    // Update theme context immediately for real-time change
    setTheme(themeValue)
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      theme: themeValue
    }))
    
    // Update user profile in database
    if (currentUser?.uid) {
      try {
        await userService.update(currentUser.uid, { theme: themeValue })
        
        // Update local user profile state
        if (userProfile) {
          setUserProfile({
            ...userProfile,
            theme: themeValue
          })
        }
      } catch (err) {
        console.error('Error updating theme:', err)
        setError('Failed to update theme preference')
      }
    }
  }

  const handlePhotoUpload = async (file: File) => {
    if (!currentUser?.uid || !auth.currentUser) return
    
    try {
      setUploadingPhoto(true)
      setError('')
      setSuccess('')
      
      console.log('📸 Profile Photo Upload Debug:', {
        userId: currentUser.uid,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        timestamp: new Date().toISOString()
      })
      
      // Upload to Firebase Storage
      const uploadResult = await StorageService.uploadUserProfilePhoto(currentUser.uid, file)
      
      console.log('✅ Photo upload successful:', uploadResult.url)
      
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, { photoURL: uploadResult.url })
      console.log('✅ Firebase Auth profile updated with new photoURL')

      // Update user profile in database
      await userService.update(currentUser.uid, { photoURL: uploadResult.url })
      console.log('✅ Firestore profile updated with new photoURL')
      
      // Update local user profile state
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          photoURL: uploadResult.url
        })
      }
      
      // Notify other components (like sidebar) that profile was updated
      window.dispatchEvent(new CustomEvent('profileUpdated', { 
        detail: { photoURL: uploadResult.url } 
      }))
      
      setSuccess('Profile photo updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
      
    } catch (err) {
      console.error('❌ Error uploading photo:', err)
      setError(err instanceof Error ? err.message : 'Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handlePhotoUpload(file)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const renderDevHelpersCard = () => {
    if (process.env.NODE_ENV === 'development') {
      return (
        <div className="space-y-4">
          <Card className="mt-6 mb-6">
            <CardHeader>
              <CardTitle>Development Helpers</CardTitle>
              <CardDescription>Actions for debugging during development.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => (window as any).callTestFunction()}>Call Test Function</Button>
              <Button variant="outline" size="sm" onClick={() => (window as any).refreshClaims()}>Refresh Claims</Button>
              <Button variant="outline" size="sm" onClick={() => (window as any).checkClaims()}>Check Claims</Button>
              <Button variant="outline" size="sm" onClick={() => (window as any).debugMemberships()}>Debug Memberships</Button>
            </CardContent>
          </Card>
          <PaletteSeedingComponent />
        </div>
      );
    }
    return null;
  };

  const handleSaveUserDefaultPalette = async (newPaletteId: string) => {
    console.log('🎨 handleSaveUserDefaultPalette called with paletteId:', newPaletteId);
    
    if (!currentUser?.uid || !userProfile) {
      console.log('❌ Missing user or profile, showing error toast');
      toast({
        title: "⚠️ Authentication Error",
        description: "User not found. Cannot save default palette.",
        variant: "destructive",
      });
      return;
    }
    
    const currentPrefs = resolvedChartPreferences;
    const updatedChartPrefs: ChartPreferences = {
      ...currentPrefs,
      displayPreferences: {
        ...currentPrefs.displayPreferences,
        defaultColorPalette: newPaletteId,
      },
    };

    try {
      setSaving(true); 
      console.log('💾 Saving chart preferences to user profile...');
      await userService.update(currentUser.uid, { chartPreferences: updatedChartPrefs });
      console.log('✅ User preferences saved successfully');
      
      setUserProfile(prev => prev ? {
        ...prev,
        chartPreferences: updatedChartPrefs,
      } : null);
      
      console.log('🎉 Showing success toast');
      toast({
        title: "🎨 Palette Saved",
        description: "Your new default color palette preference has been saved successfully.",
        variant: "fireforge",
      });
    } catch (err) {
      console.error('❌ Error updating default palette:', err);
      console.log('🚨 Showing error toast');
      toast({
        title: "⚠️ Save Failed",
        description: "Could not save your default palette preference. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Sidebar>
    )
  }

  return (
    <Sidebar>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border pb-5">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold leading-tight text-foreground">Profile</h1>
              <p className="mt-2 max-w-4xl text-sm text-muted-foreground">
                Manage your account settings and preferences.
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="w-full flex justify-center py-2">
          <SlidingToggle
            options={[
              { value: 'profile', label: 'Profile', icon: <User className="h-4 w-4" /> },
              { value: 'preferences', label: 'Preferences', icon: <Settings className="h-4 w-4" /> },
              { value: 'charts', label: 'Charts', icon: <BarChart3 className="h-4 w-4" /> },
              { value: 'colors', label: 'Colors', icon: <Palette className="h-4 w-4" /> },
              { value: 'security', label: 'Security', icon: <Shield className="h-4 w-4" /> }
            ]}
            value={activeTab}
            onChange={setActiveTab}
            className="w-full max-w-xs sm:max-w-md lg:max-w-2xl"
          />
        </div>

        {/* Content Area */}
        <div className="space-y-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Profile Header */}
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>
                    Update your personal information and profile details.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Avatar Section */}
                  <div className="flex items-center gap-4">
                    <Avatar className="h-20 w-20">
                      <AvatarImage src={(userProfile?.photoURL as string) || undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary text-lg">
                        {getInitials((userProfile?.displayName as string) || (userProfile?.email as string) || 'U')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium">{(userProfile?.displayName as string) || 'No name set'}</h3>
                      <p className="text-sm text-muted-foreground">{userProfile?.email as string}</p>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="profile-photo-upload"
                          disabled={uploadingPhoto}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={uploadingPhoto}
                          onClick={() => document.getElementById('profile-photo-upload')?.click()}
                        >
                          {uploadingPhoto ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Camera className="h-4 w-4 mr-2" />
                              Change Photo
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Profile Form */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="displayName">Display Name</Label>
                      <Input
                        id="displayName"
                        value={(formData.displayName as string) || ''}
                        onChange={(e) => handleInputChange('displayName', e.target.value)}
                        placeholder="Your display name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={userProfile?.email || ''}
                        disabled
                        className="bg-muted"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title">Job Title</Label>
                      <Input
                        id="title"
                        value={formData.title || ''}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Your job title"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phoneNumber">Phone Number</Label>
                      <Input
                        id="phoneNumber"
                        value={formData.phoneNumber || ''}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={formData.location || ''}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        placeholder="City, Country"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        value={formData.website || ''}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        placeholder="https://yourwebsite.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="timezone">Timezone</Label>
                      <Select
                        value={formData.timezone || ''}
                        onValueChange={(value) => handleInputChange('timezone', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select your timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map((timezone) => (
                            <SelectItem key={timezone.value} value={timezone.value}>
                              {timezone.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="language">Language</Label>
                        <Badge variant="secondary" className="text-xs">
                          Coming Soon
                        </Badge>
                      </div>
                      <Select
                        value={formData.language || ''}
                        onValueChange={(value) => handleInputChange('language', value)}
                        disabled
                      >
                        <SelectTrigger className="opacity-50">
                          <SelectValue placeholder="Select your language" />
                        </SelectTrigger>
                        <SelectContent>
                          {LANGUAGES.map((language) => (
                            <SelectItem key={language.value} value={language.value}>
                              {language.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio || ''}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="Tell us about yourself..."
                      rows={4}
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSaveProfile} disabled={saving}>
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && userProfile && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Preferences</CardTitle>
                  <CardDescription>
                    Choose how you want to be notified about updates and activities.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Ensure preferences object exists and has defaults */}
                  {userProfile.preferences ? (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="text-sm font-medium">Email Notifications</div>
                          <div className="text-xs text-muted-foreground">
                            Receive notifications via email
                          </div>
                        </div>
                        <Button
                          variant={userProfile.preferences.emailNotifications ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePreferenceChange('emailNotifications', !userProfile.preferences?.emailNotifications)}
                        >
                          {userProfile.preferences.emailNotifications ? 'Enabled' : 'Disabled'}
                        </Button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="text-sm font-medium">Push Notifications</div>
                          <div className="text-xs text-muted-foreground">
                            Receive push notifications in your browser
                          </div>
                        </div>
                        <Button
                          variant={userProfile.preferences.pushNotifications ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePreferenceChange('pushNotifications', !userProfile.preferences?.pushNotifications)}
                        >
                          {userProfile.preferences.pushNotifications ? 'Enabled' : 'Disabled'}
                        </Button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="text-sm font-medium">Marketing Emails</div>
                          <div className="text-xs text-muted-foreground">
                            Receive updates about new features and promotions
                          </div>
                        </div>
                        <Button
                          variant={userProfile.preferences.marketingEmails ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePreferenceChange('marketingEmails', !userProfile.preferences?.marketingEmails)}
                        >
                          {userProfile.preferences.marketingEmails ? 'Enabled' : 'Disabled'}
                        </Button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="text-sm font-medium">Weekly Digest</div>
                          <div className="text-xs text-muted-foreground">
                            Receive a weekly summary of your activity
                          </div>
                        </div>
                        <Button
                          variant={userProfile.preferences.weeklyDigest ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePreferenceChange('weeklyDigest', !userProfile.preferences?.weeklyDigest)}
                        >
                          {userProfile.preferences.weeklyDigest ? 'Enabled' : 'Disabled'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Notification preferences are not available.</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Theme Preferences</CardTitle>
                  <CardDescription>
                    Customize the appearance of your interface.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Theme</Label>
                    <SlidingToggle
                      options={[
                        { value: 'light', label: 'Light', icon: <Sun className="h-4 w-4" /> },
                        { value: 'dark', label: 'Dark', icon: <Moon className="h-4 w-4" /> },
                        { value: 'system', label: 'System', icon: <Monitor className="h-4 w-4" /> }
                      ]}
                      value={theme}
                      onChange={(value) => handleThemeChange(value)}
                      className="w-full max-w-sm"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Chart Preferences Tab */}
          {activeTab === 'charts' && userProfile && (
            <div className="space-y-6">
              <ChartPreferencesSettings
                preferences={resolvedChartPreferences}
                onSave={async (preferences: ChartPreferences) => {
                  if (!currentUser?.uid) return;
                  
                  try {
                    await userService.update(currentUser.uid, {
                      chartPreferences: preferences,
                    });
                    
                    setUserProfile(prev => prev ? {
                      ...prev,
                      chartPreferences: preferences,
                    } : null);
                    
                    setSuccess('Chart preferences updated successfully!');
                    setTimeout(() => setSuccess(''), 3000);
                  } catch (error) {
                    console.error('Error updating chart preferences:', error);
                    setError('Failed to update chart preferences');
                    throw error;
                  }
                }}
                title="Personal Chart Preferences"
                description="Customize how charts are displayed for your personal use. These settings override your organization's default preferences."
                showInheritanceInfo={true}
                inheritedFrom="Organization Settings"
              />
            </div>
          )}

          {/* Color Palettes Tab */}
          {activeTab === 'colors' && userProfile && currentUser && (
            <>
              {/* {renderDevHelpersCard()} */}
              <PaletteManagementUI 
                userId={currentUser.uid}
                organizationId={currentOrganization?.id || ''}
                currentUserChartPreferences={resolvedChartPreferences}
                onUserDefaultPaletteChange={handleSaveUserDefaultPalette}
              />
            </>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && userProfile && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Account Security</CardTitle>
                  <CardDescription>
                    Manage your account security settings and authentication.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">Email Verification</div>
                      <div className="text-xs text-muted-foreground">
                        {userProfile.emailVerified ? 'Your email is verified' : 'Please verify your email address'}
                      </div>
                    </div>
                    <Badge variant={userProfile.emailVerified ? "default" : "destructive"}>
                      {userProfile.emailVerified ? 'Verified' : 'Unverified'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">Last Login</div>
                      <div className="text-xs text-muted-foreground">
                        {userProfile.lastLoginAt ? new Date(userProfile.lastLoginAt).toLocaleString() : 'Never'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">Account Created</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(userProfile.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  )
}

export default Profile 